import { useState, useEffect, useCallback } from 'react';
import { getPatient, getDoctor, getDoctorSignatureUrl, getSubmission, getCertificate } from '../services/api/apis';
import { getBase64FromUrl } from '../helpers';
import type { Patient, Doctor, CertificateFormData } from '../types';
import type { PrescriptionFormData } from './formHooks/usePrescriptionForm';

function mapToFormData(patient: Patient, doctor?: Doctor, signature?: string): Partial<CertificateFormData> {
  return {
    patientName:
      [patient.name, patient.last_name].filter(Boolean).join(' ').trim() ||
      patient.full_name ||
      '',
    age: patient.age || '',
    gender: (patient.gender?.toLowerCase() as CertificateFormData['gender']) || '',
    docId: patient.doc_id || '',
    certificateType: patient.certificate_type || '',
    address: patient.full_address || '',
    organisation: patient.organisation || '',
    diagnosis: patient.diagnosis || '',
    noOfDays: patient.no_of_days || '',
    fromDate: patient.from_date || '',
    caretakerFullName: patient.caretaker_full_name || '',
    caretakerAge: patient.caretaker_age || '',
    identificationMark: patient.id_mark || '',
    additionalDetails: patient.additional_details || '',
    illnessDetails: patient.detail_of_illness || '',
    drName: doctor?.name || '',
    regNo: doctor?.regNo || '',
    specialization: doctor?.specialization || '',
    signature: signature || '',
    issueDate: new Date().toISOString().split('T')[0],
  };
}

export interface UseSelectedPatientReturn {
  patient: Patient | null;
  doctor: Doctor | null;
  isLoading: boolean;
  error: string | null;
  initialFormData: Partial<CertificateFormData>;
  submittedPrescription: PrescriptionFormData | null;
  refetch: () => void;
}

export function useSelectedPatient(docId: string | undefined): UseSelectedPatientReturn {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [isLoading, setIsLoading] = useState(!!docId);
  const [error, setError] = useState<string | null>(null);
  const [initialFormData, setInitialFormData] = useState<Partial<CertificateFormData>>({});
  const [submittedPrescription, setSubmittedPrescription] = useState<PrescriptionFormData | null>(null);

  const fetchData = useCallback(async () => {
    if (!docId) return;
    setIsLoading(true);
    setError(null);
    try {
      const p = await getPatient(docId);
      setPatient(p);

      let doc: Doctor | undefined;
      let signature: string | undefined;
      if (p.assigned_doctor_id) {
        try {
          doc = await getDoctor(p.assigned_doctor_id);
          setDoctor(doc);
        } catch {
          // doctor fetch failure is non-fatal — form will just have empty doctor fields
        }
        try {
          const url = await getDoctorSignatureUrl(p.assigned_doctor_id);
          if (url) signature = await getBase64FromUrl(url);
        } catch {
          // no signature uploaded — silent
        }
      }

      //let savedPrescription: any = null;
      try {
        const sub = await getSubmission(docId);
        if (sub.prescription) {
          setSubmittedPrescription(sub.prescription);
          //savedPrescription = sub.prescription;
        }
      } catch {
        // 404 = doctor hasn't submitted yet — silent
      }

      // Load previously saved certificate form data so admin edits persist
      // across page reloads. Saved data takes priority over patient metadata.
      let savedCertData: Partial<CertificateFormData> = {};
      try {
        const certResult = await getCertificate(docId);
        if (certResult?.certificate) {
          savedCertData = certResult.certificate as Partial<CertificateFormData>;
        }
      } catch {
        // no certificate saved yet — silent
      }

      const baseFormData = mapToFormData(p, doc, signature);
      // Merge: saved certificate data overrides base (patient metadata) defaults.
      // This restores drName, regNo, specialization, issueDate, content, etc.
      setInitialFormData({ ...baseFormData, ...savedCertData });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load patient');
    } finally {
      setIsLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { patient, doctor, isLoading, error, initialFormData, submittedPrescription, refetch: fetchData };
}
